import {
  CreateNewFolderOutlined,
  LinkOutlined,
  GetAppOutlined,
} from '@mui/icons-material';
import type { MenuProps } from '@mui/material';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useState } from 'react';

import { downloadAllFilesForOwner, downloadFile } from '@/utils/file/fileUtils';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';

import type { FileManagerUploadingState } from '@/modules/files/components/file-manager/FileManagerUploadingBar';

import { ConfirmDownloadDialog } from '../../dialogs/ConfirmDownloadDialog';
import { CreateFolderDialog } from '../../dialogs/CreateFolderDialog';

import { CreateUrlFileDialog } from '../../dialogs/CreateUrlFileDialog';

import { UploadZipMenuItem } from './folder-menu/UploadZipMenuItem';
import type { Segment } from './FileManagerBreadcrumbsBarSegment';
import { UploadFileMenuItem } from './folder-menu/UploadFileMenuItem';

const FILE_MANAGER_FOLDER_MENU_FOLDER_FRAGMENT = graphql(`
  fragment fileManagerFolderMenuFolderFields on File {
    ...createUrlFileDialogFolderFields
  }
`);

const FILE_MANAGER_FOLDER_MENU_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment fileManagerFolderMenuFilesForOwnerFields on FilesForOwner {
    ...createUrlFileDialogFilesForOwnerFields
  }
`);

interface Props extends MenuProps {
  folder: DocumentType<typeof FILE_MANAGER_FOLDER_MENU_FOLDER_FRAGMENT> | null;
  filesForOwner: DocumentType<
    typeof FILE_MANAGER_FOLDER_MENU_FILES_FOR_OWNER_FRAGMENT
  >;
  rootOwnerId: string;
  rootOwnerType: 'Group' | 'Service';
  canEdit: boolean;
  setUploadingState: Dispatch<SetStateAction<FileManagerUploadingState>>;
  onClose: () => void;
  segment: Segment;
  createOnly?: boolean;
}

export const FileManagerFolderMenu = ({
  folder,
  filesForOwner,
  rootOwnerId,
  rootOwnerType,
  anchorEl,
  onClose,
  segment,
  canEdit,
  createOnly,
  setUploadingState,
  transformOrigin = {
    vertical: 'top',
    horizontal: 'left',
  },
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'left',
  },
  ...other
}: Props) => {
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [createUrlFileDialogOpen, setCreateUrlFileDialogOpen] = useState(false);
  const [confirmDownloadOpen, setConfirmDownloadOpen] = useState(false);

  const handleDownload = useCallback(() => {
    const {
      fileId: segmentFileId,
      ownerId: segmentOwnerId,
      name: segmentName,
    } = segment;

    if (segmentFileId) {
      downloadFile(segmentFileId);
    } else if (segmentOwnerId && segmentName) {
      downloadAllFilesForOwner(segmentOwnerId, segmentName);
    }
  }, [segment]);

  return (
    <>
      <Menu
        {...other}
        keepMounted
        id={`${segment.fileId || 'root'}-segment-menu`}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        disableRestoreFocus
      >
        {canEdit && (
          <MenuItem
            onClick={() => {
              onClose();
              setCreateFolderDialogOpen(true);
            }}
          >
            <ListItemIcon>
              <CreateNewFolderOutlined />
            </ListItemIcon>
            <ListItemText>Create new folder</ListItemText>
          </MenuItem>
        )}
        {canEdit && (
          <UploadFileMenuItem
            segment={segment}
            setUploadingState={setUploadingState}
            onClose={onClose}
            rootOwnerId={rootOwnerId}
          />
        )}
        {canEdit && (
          <UploadZipMenuItem
            segment={segment}
            setUploadingState={setUploadingState}
            onClose={onClose}
            rootOwnerId={rootOwnerId}
          />
        )}
        {canEdit && (
          <MenuItem
            onClick={() => {
              onClose();
              setCreateUrlFileDialogOpen(true);
            }}
          >
            <ListItemIcon>
              <LinkOutlined />
            </ListItemIcon>
            <ListItemText>Add a web link</ListItemText>
          </MenuItem>
        )}
        {!createOnly && (
          <MenuItem
            onClick={() => {
              onClose();
              setConfirmDownloadOpen(true);
            }}
          >
            <ListItemIcon>
              <GetAppOutlined />
            </ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>
        )}
      </Menu>
      {segment.ownerId ? (
        <CreateFolderDialog
          owner={segment.ownerId}
          rootOwnerId={rootOwnerId}
          parentId={segment.fileId}
          parentName={segment.name ?? ''}
          onClose={() => setCreateFolderDialogOpen(false)}
          rootOwner={{ _id: rootOwnerId, type: rootOwnerType }}
          open={createFolderDialogOpen}
        />
      ) : null}
      <CreateUrlFileDialog
        folder={folder}
        filesForOwner={filesForOwner}
        open={createUrlFileDialogOpen}
        onClose={() => setCreateUrlFileDialogOpen(false)}
        rootOwnerId={rootOwnerId}
      />
      <ConfirmDownloadDialog
        open={confirmDownloadOpen}
        onClose={() => setConfirmDownloadOpen(false)}
        onConfirm={handleDownload}
      />
    </>
  );
};
